import { useState, type FormEvent } from "react";
import { useContextoApp } from "@/app/contexto";
import { useApiResource } from "@/hooks/useApiResource";
import { useAsyncAction } from "@/hooks/useAsyncAction";
import { api, type Cronograma, type ItemComputo, type Presupuesto, type TareaCronograma } from "@/lib/api";
import { validarInicio } from "@/lib/avance";
import {
  aPayloadPredecesoras,
  aPayloadPrograma,
  filaPredecesoraNueva,
  filasGantt,
  formTarea,
  guardarVista,
  hayErroresTarea,
  leerVista,
  marcasGantt,
  nombresTareas,
  opcionesPredecesora,
  opcionesRubro,
  validarTarea,
  type ErroresTarea,
  type FilaPredecesora,
  type FormTarea,
  type VistaCronograma,
} from "@/lib/cronograma";

type Dialogo = { tipo: "tarea"; tarea: TareaCronograma } | { tipo: "inicio" } | { tipo: "nueva" } | null;

const SIN_ERRORES: ErroresTarea = { predecesoras: {} };
type ErroresNueva = Partial<Record<"rubroId" | "descripcion" | "duracion", string>>;

/**
 * Lógica del cronograma por ítems (sin JSX, sólo admin): cada ítem del presupuesto es una tarea con duración en días
 * hábiles, "no antes de" y predecesoras; el backend calcula fechas y el camino crítico. Fijar la fecha de inicio y
 * agregar una tarea (un ítem sin precio en un rubro). Se ve como lista por rubro o como Gantt de toda la obra (Mati,
 * 2026-10-28); la vista elegida queda en el navegador.
 */
export function useCronogramaTab() {
  const { fid } = useContextoApp();
  const base = `/api/fideicomisos/${fid}/cronograma`;
  const recurso = useApiResource<Cronograma>(base);
  const presupuesto = useApiResource<Presupuesto>(`/api/fideicomisos/${fid}/presupuesto`);
  const [dialogo, setDialogo] = useState<Dialogo>(null);
  const [form, setForm] = useState<FormTarea>(() => formTarea());
  const [inicio, setInicio] = useState("");
  const [nueva, setNueva] = useState({ rubroId: "", descripcion: "", duracion: "" });
  const [mostrarErrores, setMostrarErrores] = useState(false);
  const [vista, setVista] = useState<VistaCronograma>(leerVista);

  const accionTarea = useAsyncAction(async (t: TareaCronograma, f: FormTarea) => {
    await api.patch<Cronograma>(`${base}/items/${t.id}`, aPayloadPrograma(f));
    return api.put<Cronograma>(`${base}/items/${t.id}/predecesoras`, aPayloadPredecesoras(f));
  });
  const accionInicio = useAsyncAction((valor: string | null) => api.put<Cronograma>(`${base}/inicio`, { inicio: valor }));
  const accionNueva = useAsyncAction(async (rubroId: string, descripcion: string, duracion: string) => {
    const item = await api.post<ItemComputo>(`/api/fideicomisos/${fid}/presupuesto/rubros/${rubroId}/items`, { descripcion });
    return duracion.trim() ? api.patch<Cronograma>(`${base}/items/${item.id}`, { duracion: Number(duracion) }) : true;
  });

  const c = recurso.data ?? null;
  const errores = validarTarea(form);
  const errorInicio = validarInicio(inicio);
  const erroresNueva: ErroresNueva = {
    rubroId: nueva.rubroId ? undefined : "Elegí el rubro",
    descripcion: nueva.descripcion.trim() ? undefined : "Escribí qué es",
    duracion: !nueva.duracion.trim() || /^\d{1,4}$/.test(nueva.duracion.trim()) ? undefined : "Días hábiles, de 0 a 9999",
  };
  const cerrar = () => setDialogo(null);

  const abrirTarea = (t: TareaCronograma) => {
    setForm(formTarea(t));
    setMostrarErrores(false);
    accionTarea.limpiarError();
    setDialogo({ tipo: "tarea", tarea: t });
  };

  const guardarTarea = async (e: FormEvent) => {
    e.preventDefault();
    setMostrarErrores(true);
    if (dialogo?.tipo !== "tarea" || hayErroresTarea(errores)) return;
    if (await accionTarea.run(dialogo.tarea, form)) {
      cerrar();
      recurso.recargar();
    }
  };

  const guardarInicio = async (e: FormEvent) => {
    e.preventDefault();
    if (errorInicio) return;
    if (await accionInicio.run(inicio || null)) {
      cerrar();
      recurso.recargar();
    }
  };

  const guardarNueva = async (e: FormEvent) => {
    e.preventDefault();
    setMostrarErrores(true);
    if (Object.values(erroresNueva).some(Boolean)) return;
    if (await accionNueva.run(nueva.rubroId, nueva.descripcion.trim(), nueva.duracion)) {
      cerrar();
      recurso.recargar();
    }
  };

  const cambiarPredecesora = (i: number, cambio: Partial<FilaPredecesora>) =>
    setForm((f) => ({ ...f, predecesoras: f.predecesoras.map((p, j) => (j === i ? { ...p, ...cambio } : p)) }));

  return {
    cronograma: c,
    vista,
    elegirVista: (v: VistaCronograma) => {
      setVista(v);
      guardarVista(v);
    },
    filasGantt: c ? filasGantt(c) : [],
    marcasGantt: c ? marcasGantt(c) : [],
    nombres: c ? nombresTareas(c) : new Map<number, string>(),
    cargando: recurso.loading && !c,
    error: c ? null : (recurso.error?.message ?? null),
    dialogo,
    cerrar,

    abrirTarea,
    form,
    errores: mostrarErrores ? errores : SIN_ERRORES,
    cambiar: <K extends keyof FormTarea>(campo: K, valor: FormTarea[K]) => setForm((f) => ({ ...f, [campo]: valor })),
    agregarPredecesora: () => setForm((f) => ({ ...f, predecesoras: [...f.predecesoras, filaPredecesoraNueva()] })),
    cambiarPredecesora,
    sacarPredecesora: (i: number) => setForm((f) => ({ ...f, predecesoras: f.predecesoras.filter((_, j) => j !== i) })),
    opcionesPredecesora: c && dialogo?.tipo === "tarea" ? opcionesPredecesora(c, dialogo.tarea.id) : [],
    guardarTarea,
    guardandoTarea: accionTarea.loading,
    errorTarea: accionTarea.error,

    abrirInicio: () => {
      setInicio(c?.inicio ?? "");
      accionInicio.limpiarError();
      setDialogo({ tipo: "inicio" });
    },
    inicio,
    setInicio,
    errorInicio,
    guardarInicio,
    guardandoInicio: accionInicio.loading,
    errorGuardarInicio: accionInicio.error,

    abrirNueva: () => {
      setNueva({ rubroId: "", descripcion: "", duracion: "" });
      setMostrarErrores(false);
      accionNueva.limpiarError();
      setDialogo({ tipo: "nueva" });
    },
    nueva,
    cambiarNueva: (campo: keyof typeof nueva, valor: string) => setNueva((n) => ({ ...n, [campo]: valor })),
    erroresNueva: mostrarErrores ? erroresNueva : ({} as ErroresNueva),
    opcionesRubro: c ? opcionesRubro(c, (presupuesto.data?.items ?? []).filter((i) => i.rubro.activo && i.rubro.clave !== "fee").map((i) => i.rubro)) : [],
    guardarNueva,
    guardandoNueva: accionNueva.loading,
    errorNueva: accionNueva.error,
  };
}
